import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Lock, Shield, Bell, BellOff, Smartphone, RefreshCw, Fingerprint, Monitor, Tablet, Globe, Trash2, LogOut } from "lucide-react";
import { isPushSupported, getPushPermissionState, subscribeToPush, unsubscribeFromPush, registerServiceWorker, resubscribeToPush } from "@/lib/pushNotifications";
import { useConfirm } from "@/components/ConfirmDialog";
import { startRegistration } from "@simplewebauthn/browser";

export default function MyProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ name: "", email: "" });
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [pushSupported] = useState(isPushSupported());
  const [pushPermission, setPushPermission] = useState(getPushPermissionState());
  const [pushStatus, setPushStatus] = useState({ subscribed: false, device_count: 0 });
  const [pushLoading, setPushLoading] = useState(false);
  const confirm = useConfirm();
  const [sessions, setSessions] = useState([]);
  const [passkeys, setPasskeys] = useState([]);
  const [registeringPasskey, setRegisteringPasskey] = useState(false);

  const fetchPushStatus = useCallback(() => {
    API.get("/push/status").then(r => setPushStatus(r.data)).catch(() => {});
  }, []);

  const fetchSessions = useCallback(() => {
    API.get("/auth/sessions").then(r => setSessions(r.data.sessions || [])).catch(() => {});
  }, []);

  const fetchPasskeys = useCallback(() => {
    API.get("/auth/webauthn/credentials").then(r => setPasskeys(r.data.credentials || [])).catch(() => {});
  }, []);

  useEffect(() => {
    API.get("/auth/profile").then(r => setProfile(r.data)).catch(console.error);
    fetchPushStatus();
    fetchSessions();
    fetchPasskeys();
  }, [fetchPushStatus, fetchSessions, fetchPasskeys]);

  const handleEnablePush = async () => {
    setPushLoading(true);
    try {
      await registerServiceWorker();
      // Force fresh subscription to avoid stale FCM endpoints
      const sub = await resubscribeToPush();
      if (sub) {
        setPushPermission("granted");
        fetchPushStatus();
        toast.success("Push notifications enabled");
      } else {
        toast.error("Could not enable push notifications. Check browser permissions.");
      }
    } catch (e) { toast.error("Failed to enable push notifications"); }
    finally { setPushLoading(false); }
  };

  const handleDisablePush = async () => {
    setPushLoading(true);
    try {
      await unsubscribeFromPush();
      fetchPushStatus();
      toast.success("Push notifications disabled for this device");
    } catch (e) { toast.error("Failed to disable push notifications"); }
    finally { setPushLoading(false); }
  };

  const handleTestPush = async () => {
    try {
      const r = await API.post("/push/test");
      if (r.data.sent > 0) {
        toast.success(`Test notification sent to ${r.data.sent} device${r.data.sent > 1 ? "s" : ""}`);
      } else {
        toast.warning("No notifications delivered. Try disabling and re-enabling push.");
      }
      if (r.data.expired_cleaned > 0) {
        toast.info(`${r.data.expired_cleaned} stale subscription${r.data.expired_cleaned > 1 ? "s" : ""} cleaned up`);
        fetchPushStatus();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send test notification"); }
  };

  const handleCleanStale = async () => {
    try {
      const r = await API.post("/push/clean-stale");
      if (r.data.removed > 0) {
        toast.success(`Removed ${r.data.removed} stale subscription${r.data.removed > 1 ? "s" : ""}. ${r.data.active} active device${r.data.active !== 1 ? "s" : ""} remaining.`);
        fetchPushStatus();
      } else {
        toast.info(`All ${r.data.active} subscription${r.data.active !== 1 ? "s" : ""} are active. No stale devices found.`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to clean subscriptions"); }
  };

  const handleRefreshPush = async () => {
    setPushLoading(true);
    try {
      await registerServiceWorker();
      const sub = await resubscribeToPush();
      if (sub) {
        fetchPushStatus();
        toast.success("Push subscription refreshed — try sending a test notification");
      } else {
        toast.error("Could not refresh subscription. Check browser permissions.");
      }
    } catch (e) { toast.error("Failed to refresh push subscription"); }
    finally { setPushLoading(false); }
  };

  // ─── Session handlers ───
  const killSession = async (sessionId) => {
    const ok = await confirm({ title: "End Session", description: "Log out this device? The user will need to sign in again.", confirmText: "End Session", variant: "warning" });
    if (!ok) return;
    try {
      await API.delete(`/auth/sessions/${sessionId}`);
      toast.success("Session ended");
      fetchSessions();
    } catch { toast.error("Failed to end session"); }
  };

  const killAllOtherSessions = async () => {
    const ok = await confirm({ title: "Logout All Other Devices", description: `End ${sessions.length - 1} other session(s)? Those devices will need to sign in again.`, confirmText: "Logout All Others", variant: "warning" });
    if (!ok) return;
    try {
      const r = await API.delete("/auth/sessions");
      toast.success(r.data.message);
      fetchSessions();
    } catch { toast.error("Failed to end sessions"); }
  };

  // ─── Passkey handlers ───
  const registerPasskey = async () => {
    setRegisteringPasskey(true);
    try {
      const optionsRes = await API.post("/auth/webauthn/register/options");
      const options = optionsRes.data;
      const credential = await startRegistration({ optionsJSON: options });
      await API.post("/auth/webauthn/register/verify", credential);
      toast.success("Passkey registered — you can now sign in with your device");
      fetchPasskeys();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to register passkey";
      if (msg.includes("NotAllowedError") || msg.includes("cancelled")) {
        toast.info("Passkey setup cancelled");
      } else {
        toast.error(msg);
      }
    } finally { setRegisteringPasskey(false); }
  };

  const deletePasskey = async (credId) => {
    const ok = await confirm({ title: "Remove Passkey", description: "Remove this passkey? You'll need to set it up again to use biometric login on this device.", confirmText: "Remove", variant: "destructive" });
    if (!ok) return;
    try {
      await API.delete(`/auth/webauthn/credentials/${credId}`);
      toast.success("Passkey removed");
      fetchPasskeys();
    } catch { toast.error("Failed to remove passkey"); }
  };

  useEffect(() => {
    API.get("/auth/profile").then(r => setProfile(r.data)).catch(console.error);
  }, []);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await API.put("/auth/profile", { name: profile.name, email: profile.email });
      toast.success("Profile updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (passwords.new_password !== passwords.confirm_password) { toast.error("Passwords don't match"); return; }
    if (passwords.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setChangingPw(true);
    try {
      await API.put("/auth/password", { current_password: passwords.current_password, new_password: passwords.new_password });
      toast.success("Password changed");
      setPasswords({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change password"); }
    finally { setChangingPw(false); }
  };

  const inputClass = "bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
  const labelClass = "text-xs font-medium text-slate-400 mb-1.5 block";

  return (
    <div data-testid="my-profile" className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">My Profile</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your account details</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="border-b border-slate-800 py-3 px-4">
          <CardTitle className="font-heading text-base font-semibold text-slate-100 flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-500" strokeWidth={1.5} /> Account Info
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-800">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center"><User className="w-5 h-5 text-blue-500" strokeWidth={1.5} /></div>
            <div>
              <p className="text-sm font-medium text-slate-200">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.role} — {user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="border-b border-slate-800 py-3 px-4">
          <CardTitle className="font-heading text-base font-semibold text-slate-100 flex items-center gap-2">
            <User className="w-4 h-4 text-blue-500" strokeWidth={1.5} /> Edit Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div><Label className={labelClass}>Name</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={inputClass} data-testid="profile-name" /></div>
          <div>
            <Label className={labelClass}>Email</Label>
            <Input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className={inputClass} data-testid="profile-email" />
            <p className="text-[10px] text-slate-600 mt-1">Changing your email will update your login credentials</p>
          </div>
          <Button onClick={handleSaveProfile} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="save-profile-btn">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="border-b border-slate-800 py-3 px-4">
          <CardTitle className="font-heading text-base font-semibold text-slate-100 flex items-center gap-2">
            <Lock className="w-4 h-4 text-blue-500" strokeWidth={1.5} /> Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div><Label className={labelClass}>Current Password</Label><Input type="password" value={passwords.current_password} onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} className={inputClass} data-testid="current-password" /></div>
          <div><Label className={labelClass}>New Password</Label><Input type="password" value={passwords.new_password} onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} className={inputClass} data-testid="new-password" /></div>
          <div><Label className={labelClass}>Confirm Password</Label><Input type="password" value={passwords.confirm_password} onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })} className={inputClass} data-testid="confirm-password" /></div>
          <Button onClick={handleChangePassword} disabled={changingPw} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="change-password-btn">
            {changingPw ? "Changing..." : "Change Password"}
          </Button>
        </CardContent>
      </Card>

      {/* Push Notifications */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="border-b border-slate-800 py-4 px-5">
          <CardTitle className="font-heading text-base font-semibold text-slate-100 flex items-center gap-2.5">
            <Bell className="w-5 h-5 text-blue-400" strokeWidth={1.5} /> Push Notifications
          </CardTitle>
          <p className="text-sm text-slate-400 mt-1">Get notified about approvals, reminders, wins, and important updates.</p>
        </CardHeader>
        <CardContent className="p-5 space-y-5">
          {!pushSupported ? (
            <div className="flex items-center gap-3 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
              <BellOff className="w-5 h-5 text-amber-400 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-200">Not Supported</p>
                <p className="text-xs text-amber-300/70 mt-0.5">Push notifications are not supported on this browser. Use Safari 16.4+ on iOS or a modern browser on other devices.</p>
              </div>
            </div>
          ) : pushPermission === "denied" ? (
            <div className="flex items-center gap-3 p-4 bg-rose-500/10 rounded-lg border border-rose-500/20">
              <BellOff className="w-5 h-5 text-rose-400 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-rose-200">Notifications Blocked</p>
                <p className="text-xs text-rose-300/70 mt-0.5">Push notifications have been blocked. Please enable them in your browser/device settings.</p>
              </div>
            </div>
          ) : pushStatus.subscribed ? (
            <>
              {/* Enabled State */}
              <div className="flex items-center gap-3.5 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20" data-testid="push-enabled-status">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
                  <Bell className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-emerald-200">Notifications Enabled</p>
                  <p className="text-xs text-emerald-300/70 mt-0.5">You'll receive push notifications for important updates.</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2.5 flex-wrap">
                <Button variant="outline" size="sm" onClick={handleTestPush} className="border-slate-700 text-slate-300 hover:bg-slate-800 h-9 text-xs" data-testid="test-push-btn">
                  <Bell className="w-3.5 h-3.5 mr-1.5" />Send Test
                </Button>
                <Button variant="outline" size="sm" onClick={handleRefreshPush} disabled={pushLoading} className="border-slate-700 text-slate-300 hover:bg-slate-800 h-9 text-xs" data-testid="refresh-push-btn">
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${pushLoading ? "animate-spin" : ""}`} />{pushLoading ? "Refreshing..." : "Refresh"}
                </Button>
                <Button variant="outline" size="sm" onClick={handleDisablePush} disabled={pushLoading} className="border-slate-700 text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/30 h-9 text-xs" data-testid="disable-push-btn">
                  <BellOff className="w-3.5 h-3.5 mr-1.5" />Disable
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Disabled State */}
              <div className="flex items-center gap-3.5 p-4 bg-slate-800/50 rounded-lg border border-slate-800" data-testid="push-disabled-status">
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
                  <BellOff className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-300">Notifications Disabled</p>
                  <p className="text-xs text-slate-500 mt-0.5">Enable to receive approval alerts, project updates, and important reminders.</p>
                </div>
              </div>

              <Button size="sm" onClick={handleEnablePush} disabled={pushLoading} className="bg-blue-600 hover:bg-blue-500 text-white h-9 text-xs" data-testid="enable-push-btn">
                <Bell className="w-3.5 h-3.5 mr-1.5" />{pushLoading ? "Enabling..." : "Enable Notifications"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Passkey / Biometric Login */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="border-b border-slate-800 py-4 px-5">
          <CardTitle className="font-heading text-base font-semibold text-slate-100 flex items-center gap-2.5">
            <Fingerprint className="w-5 h-5 text-blue-400" /> Passkey / Biometric Login
          </CardTitle>
          <p className="text-sm text-slate-400 mt-1">Sign in using Face ID, fingerprint, or device password — no password needed.</p>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          {passkeys.length > 0 ? (
            <>
              <div className="space-y-2">
                {passkeys.map((pk) => (
                  <div key={pk.id} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-800" data-testid={`passkey-${pk.id}`}>
                    <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                      <Fingerprint className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-200">{pk.device_name || "Device"}</p>
                      <p className="text-[10px] text-slate-500">Added {new Date(pk.created_at).toLocaleDateString()} &middot; Last used {new Date(pk.last_used).toLocaleDateString()}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deletePasskey(pk.id)} className="h-8 px-2 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10" data-testid={`remove-passkey-${pk.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={registerPasskey} disabled={registeringPasskey} className="border-slate-700 text-slate-300 hover:bg-slate-800 h-9 text-xs" data-testid="add-another-passkey-btn">
                <Fingerprint className="w-3.5 h-3.5 mr-1.5" />{registeringPasskey ? "Setting up..." : "Add Another Device"}
              </Button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3.5 p-4 bg-slate-800/50 rounded-lg border border-slate-800" data-testid="passkey-empty-state">
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
                  <Fingerprint className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-300">No Passkeys Set Up</p>
                  <p className="text-xs text-slate-500 mt-0.5">Set up a passkey to sign in with Face ID, fingerprint, or device password.</p>
                </div>
              </div>
              <Button size="sm" onClick={registerPasskey} disabled={registeringPasskey} className="bg-blue-600 hover:bg-blue-500 text-white h-9 text-xs" data-testid="setup-passkey-btn">
                <Fingerprint className="w-3.5 h-3.5 mr-1.5" />{registeringPasskey ? "Setting up..." : "Set Up Passkey"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Active Sessions */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="border-b border-slate-800 py-4 px-5 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading text-base font-semibold text-slate-100 flex items-center gap-2.5">
              <Monitor className="w-5 h-5 text-blue-400" /> Active Sessions
            </CardTitle>
            <p className="text-sm text-slate-400 mt-1">Devices currently logged into your account.</p>
          </div>
          {sessions.length > 1 && (
            <Button variant="outline" size="sm" onClick={killAllOtherSessions} className="border-slate-700 text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/30 h-8 text-xs shrink-0" data-testid="logout-all-others-btn">
              <LogOut className="w-3.5 h-3.5 mr-1.5" />Logout All Others
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-5 space-y-2">
          {sessions.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No active sessions found.</p>
          ) : (
            sessions.map((s) => {
              const DeviceIcon = s.device === "Mobile" ? Smartphone : s.device === "Tablet" ? Tablet : Monitor;
              const isCurrent = s.is_current === true;
              return (
                <div key={s.id} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${isCurrent ? "bg-blue-500/5 border-blue-500/20" : "bg-slate-800/50 border-slate-800"}`} data-testid={`session-${s.id}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isCurrent ? "bg-blue-500/10" : "bg-slate-800"}`}>
                    <DeviceIcon className={`w-4 h-4 ${isCurrent ? "text-blue-400" : "text-slate-500"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-200">{s.browser || "Unknown Browser"}</p>
                      {isCurrent && <span className="text-[9px] font-semibold text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded">This Device</span>}
                      {s.auth_method === "passkey" && <span className="text-[9px] font-semibold text-violet-400 bg-violet-500/10 px-1.5 py-0.5 rounded">Passkey</span>}
                    </div>
                    <p className="text-[10px] text-slate-500">{s.os || "Unknown OS"} &middot; {s.ip || "Unknown IP"} &middot; {s.last_active ? new Date(s.last_active).toLocaleString() : "—"}</p>
                  </div>
                  {!isCurrent && (
                    <Button variant="ghost" size="sm" onClick={() => killSession(s.id)} className="h-8 px-2 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10" data-testid={`kill-session-${s.id}`}>
                      <LogOut className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
